/*
  ShareWizard
  This is the wizard used to share forms and documents
*/

// Angular Dependencies
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { MatRow } from '@angular/material/table';
import { ID } from '@datorama/akita';
import { DocumentSearchDialog } from '@app/shared/components/dialogs/document-search/document-search.dialog';
import {
  DocumentService,
  EntityService,
  FormService,
  RequestService,
  VehicleService
} from '@app/shared/services';
import { EntitiesService } from '@state/entities';
import { environment } from '@env/environment';
import { FormLayoutService } from '@app/state/form-layout';
import { FroalaHelper } from '@app/shared/services/vendor';
import { InputGridComponent } from '@app/shared/components/input-grid/input-grid.component';
import { Member, MembershipsQuery } from '@state/memberships';
import { StepperComponent } from '@app/shared/components/stepper/stepper.component';
import { UserQuery } from '@state/user';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-share-wizard',
  styleUrls: ['./share-wizard.component.scss'],
  templateUrl: './share-wizard.component.html'
})
export class ShareWizardComponent implements OnInit {
  readonly environment = environment;
  readonly SCOPE_VEHICLE = 'vehicle';
  readonly SCOPE_ORGANIZATION = 'organization';

  ASSIGNABLE_SELECTABLES = ['entity', 'vehicle'];
  disabledSubmitButton = false;
  displayedColumns: string[] = ['select', 'name', 'investmentAccount', 'totalDue'];
  documentsToShare: Array<any> = [];
  emailMessage;
  emailSubject;
  entities: Array<any> = [];
  formsToShare: Array<any> = [];
  isAdmin$ = this.userQuery.isAdmin$;
  organizationRecipients: Array<any> = [];
  scope: string; // vehicle|organization
  scopeId: string; // only applies if scope is vehicle
  sendEmail = false;
  sentRecipients: Array<any> = [];
  vehicleInvestmentRecipients: Array<any> = [];
  vehicleInvestmentRecipientsSelection = new SelectionModel<any>(true, []);
  vehicles: Array<any> = [];

  @ViewChild('stepper') stepper: StepperComponent;
  @ViewChild('organizationRecipientsInputGrid') organizationRecipientsInputGrid: InputGridComponent;

  get organizationRecipientsTrimmed(): any[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.organizationRecipientsInputGrid ? this.organizationRecipientsInputGrid.rowDataTrimmed : [];
  }

  get counterSignerRoles(): any[] {
    let roles: any = [];
    this.formsToShare.forEach((form) => {
      roles.push(form.field_roles);
    });

    roles = roles.flat();
    // filter out duplicates and only include counter roles
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    roles = roles.filter((role, pos) => roles.indexOf(role) === pos && role.includes('counter'));
    // only allow roles for as many eligible counter signers
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return roles.slice(0, this.counterEligibleOrgMembers.length);
  }

  get organizationId(): ID {
    return this.membershipsQuery.organizationId;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private formLayoutService: FormLayoutService,
    private membershipsQuery: MembershipsQuery,
    private titleService: Title,
    private userQuery: UserQuery,
    public dialog: MatDialog,
    public documentService: DocumentService,
    public entityService: EntityService,
    public entitiesService: EntitiesService,
    public formService: FormService,
    public froalaHelper: FroalaHelper,
    public requestService: RequestService,
    public vehicleService: VehicleService
  ) {}

  counterEligibleOrgMembers: Member[] = [];
  counterSigners: { [key: string]: Member } = {
    counter_1: undefined,
    counter_2: undefined,
    counter_3: undefined
  };

  ngOnInit() {
    this.titleService.setTitle('Share | Flow');

    this.formLayoutService.setAllAttributes({
      controls: [],
      title: 'Share'
    });

    this.activatedRoute.queryParams.subscribe({
      next: (params: Params) => {
        this.scope = params.scope || this.SCOPE_ORGANIZATION;
        if (this.scope === this.SCOPE_VEHICLE) {
          this.scopeId = params.scope_id;
          this.getVehicleRecipients(params.recipients || null);
        } else {
          // Only get vehicles if in organization scope
          this.getAvailableVehicles();
        }

        let shareableType: string = params.shareable_type;
        let shareables: string[] = params.shareables;
        this.parseShareablesFromParams(shareableType, shareables);
      }
    });

    this.getAvailableEntities();

    let entityId = this.membershipsQuery.entityId;
    this.entitiesService.getMembers$({ entity_id: entityId, include_invisible: true }).subscribe({
      next: (members: Member[]) => {
        this.counterEligibleOrgMembers = members;
      }
    });
  }

  checkIfMemberSelected(member: Member): boolean {
    return Object.values(this.counterSigners).includes(member);
  }

  // To set ngModel. Get the key of the counterSigner object based on the index
  counterSignersKey(i: number): string {
    return `counter_${i + 1}`;
  }

  counterSignerDisabled(i: number): boolean {
    if (i === 0) {
      return false;
    } else if (this.counterSigners[this.counterSignersKey(i - 1)]) {
      return false;
    }

    return true;
  }

  openDocumentSearchDialog(type: string): void {
    const dialogRef = this.dialog.open(DocumentSearchDialog, {
      data: {
        organizationId: this.organizationId,
        scope: this.scope,
        scopeId: this.scopeId,
        type
      },
      width: '800px'
    });

    dialogRef.afterClosed().subscribe({
      next: (results) => {
        if (results == null) return;

        const currentCount = this.formsToShare.length;
        results.forEach((element) => {
          if (!this.formsToShare.some((e) => e.id === element.id)) {
            element.position = currentCount + 1;
            this.formsToShare.push(element);
          }
        });
      }
    });
  }

  stepperNavigateNext(): void {
    this.stepper.navigateNext();
  }
  stepperNavigateBack(): void {
    this.stepper.navigateBack();
  }

  parseShareablesFromParams(shareableType: string, shareables: string[]): void {
    if (shareableType == null) return;

    // Query params can come over as an array or a single value
    shareables = Array.isArray(shareables) ? shareables : [shareables];
    this.formService.getForms(this.organizationId, this.scope, this.scopeId, shareables, {
      success: (data) => {
        this.formsToShare = this.formsToShare.concat(data);
      }
    });
  }

  getVehicleRecipients(recipientIds: any): void {
    if (recipientIds !== null) {
      recipientIds = Array.isArray(recipientIds) ? recipientIds : [recipientIds];
    }

    this.vehicleService.getInvestmentContacts(this.scopeId, {
      success: (data) => {
        this.vehicleInvestmentRecipients = data;
        if (recipientIds !== null) {
          recipientIds.forEach((recipientId) => {
            let recipientIndex = this.vehicleInvestmentRecipients.findIndex(
              (element) => element.id === recipientId
            );

            if (recipientIndex > -1) {
              this.vehicleInvestmentRecipientsSelection.select(
                this.vehicleInvestmentRecipients[recipientIndex]
              );
            }
          });
        }
      }
    });
  }

  getAvailableVehicles(): void {
    this.vehicleService.getVehicles(this.organizationId, {
      error: () => {},
      success: (data) => {
        this.vehicles = data;
      }
    });
  }

  getAvailableEntities(type?: string): void {
    if (!type) type = null;
    this.entityService.getEntities(this.organizationId, type, {
      success: (data: any) => {
        this.entities = data;
      }
    });
  }

  // Utility method for drag/drop reorder
  onDropObject(event: CdkDragDrop<string[]>, listToReorder: any[]): void {
    moveItemInArray(listToReorder, event.previousIndex, event.currentIndex);
  }

  // Stepper Validation methods
  hasValidFormsOrDocs(): boolean {
    let hasAssignableIds = true;
    this.formsToShare.forEach((form) => {
      let assignableTo: string = form.assignable_to;
      if (
        this.ASSIGNABLE_SELECTABLES.includes(assignableTo) &&
        (this.entities.length > 0 || this.vehicles.length > 0)
      ) {
        if (!form.assignable_id) hasAssignableIds = false;
      }
    });
    return hasAssignableIds && (this.formsToShare.length > 0 || this.documentsToShare.length > 0);
  }

  hasValidRecipients(): boolean {
    let hasCounterSigners = true;
    for (const role of this.counterSignerRoles) {
      if (!this.counterSigners[role]) {
        hasCounterSigners = false;
        break;
      } else {
        hasCounterSigners = true;
      }
    }

    if (this.scope === this.SCOPE_VEHICLE) {
      return this.vehicleInvestmentRecipientsSelection.hasValue() && hasCounterSigners;
    } else if (this.scope === this.SCOPE_ORGANIZATION) {
      return this.organizationRecipients.length > 1 && hasCounterSigners;
    }
  }

  hasValidDocsAndValidRecipients(): boolean {
    return this.hasValidFormsOrDocs() && this.hasValidRecipients();
  }

  // Vehicle Recipient Table Methods
  // Whether the number of selected elements matches the total number of rows.
  isAllSelected(): boolean {
    const numSelected = this.vehicleInvestmentRecipientsSelection.selected.length;
    const numRows = this.vehicleInvestmentRecipients.length;
    return numSelected === numRows;
  }

  isRowSelected(row: MatRow): boolean {
    return this.vehicleInvestmentRecipientsSelection.isSelected(row);
  }

  // Selects all rows if they are not all selected; otherwise clear selection.
  masterToggle(): void {
    if (this.isAllSelected()) {
      this.vehicleInvestmentRecipientsSelection.clear();
    }
    this.vehicleInvestmentRecipients.forEach((row) => this.vehicleInvestmentRecipientsSelection.select(row));
  }

  submit(): void {
    let formsParams = [];
    let usersParams = [];

    this.disabledSubmitButton = true;

    // Transpose forms to share
    this.formsToShare.forEach((element) => {
      let form = {
        assignable_id: element.assignable_id,
        form_id: element.id
      };
      formsParams.push(form);
    });

    // Transpose recipients
    if (this.scope === this.SCOPE_VEHICLE) {
      this.vehicleInvestmentRecipientsSelection.selected.forEach((element) => {
        let user = { investment_id: element.investment_id };
        usersParams.push(user);
      });
    } else if (this.scope === this.SCOPE_ORGANIZATION) {
      this.organizationRecipients.forEach((element) => {
        let user = {
          email: element.email,
          name: element.name
        };

        usersParams.push(user);
      });
    }

    let requestParams = {
      counter_signers: this.counterSigners,
      forms: formsParams,
      message: this.emailMessage,
      scope: this.scope,
      scope_id: this.scopeId,
      skip_email: !this.sendEmail,
      subject: this.emailSubject,
      users: usersParams
    };

    this.requestService.create(this.organizationId, requestParams, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      success: (data) => (this.sentRecipients = data)
    });
  }

  removeForm(formIdToRemove: ID): void {
    let formIndex = this.formsToShare.findIndex((form) => form.id === formIdToRemove);

    if (formIndex > -1) this.formsToShare.splice(formIndex, 1);
  }
}
